import { useCallback } from 'react';
import { FieldErrors, FieldPath, FieldPathValue, useFormContext } from 'react-hook-form';
import {
	EntertainerApplicationContract,
	EntertainerApplicationFieldErrors,
	EntertainerApplicationFormState,
} from './EntertainerApplicationTypes';

export const fieldLabels: Record<string, string> = {
	EntertainerName: 'Name of act',
	ContactFirstName: 'Contact first name',
	ContactLastName: 'Contact last name',
	ContactEmailAddress: 'Email',
	ContactPrimaryPhoneNumber: 'Phone number',
	EntertainerStreetAddress1: 'Street address 1',
	EntertainerAddressCity: 'Suburb or city',
	EntertainerAddressState: 'State or territory',
	EntertainerAddressPostcode: 'Postcode',
	EntertainerABN: 'ABN',
	EntertainerActFormats: 'Act formats',
	EntertainerGenres: 'Musical genres',
	EntertainerSpecialties: 'Specialties',
	EntertainerMinimumPerformerCount: 'Minimum performers',
	EntertainerMaximumPerformerCount: 'Maximum performers',
	EntertainerAvailability: 'Market availability',
	EntertainerPerformanceType: 'Performance preference',
	EntertainerPowerSlotsRequired: 'Power outlets',
	EntertainerInsuranceExpiry: 'Insurance expiry',
	EntertainerWebsite: 'Website or social profile',
	EntertainerDescription: 'Description of your act',
	customFile: 'Image of your act',
};

export const promotionLinkFields = [
	'EntertainerWebsite',
	'EntertainerFacebook',
	'EntertainerInstagram',
	'EntertainerSoundcloud',
	'EntertainerYoutubeChannel',
] as const;

type PromotionLinkField = (typeof promotionLinkFields)[number];

const formFieldNames = new Set<keyof EntertainerApplicationFormState>([
	'performerRangeMode',
	'EntertainerName',
	'ContactFirstName',
	'ContactLastName',
	'ContactEmailAddress',
	'ContactPrimaryPhoneNumber',
	'EntertainerStreetAddress1',
	'EntertainerStreetAddress2',
	'EntertainerAddressCity',
	'EntertainerAddressState',
	'EntertainerAddressPostcode',
	'EntertainerABN',
	'EntertainerActFormats',
	'EntertainerGenres',
	'EntertainerSpecialties',
	'EntertainerMinimumPerformerCount',
	'EntertainerMaximumPerformerCount',
	'EntertainerWebsite',
	'EntertainerFacebook',
	'EntertainerInstagram',
	'EntertainerSoundcloud',
	'EntertainerYoutubeChannel',
	'EntertainerDescription',
	'EntertainerRequirements',
	'EntertainerInsuranceCompany',
	'EntertainerInsurancePolicyNumber',
	'EntertainerInsuranceExpiry',
	'EntertainerPowerSlotsRequired',
	'EntertainerAvailability',
	'EntertainerPerformanceType',
	'customFile',
	'botDetector',
]);

export const formatList = (values: string[]) => {
	if (values.length < 2) return values.join('');
	return `${values.slice(0, -1).join(', ')} or ${values[values.length - 1]}`;
};

export const formatMegabytes = (bytes: number) => `${Math.round(bytes / (1024 * 1024))} MB`;

export const createInitialForm = (contract: EntertainerApplicationContract): EntertainerApplicationFormState => ({
	performerRangeMode: 'one-size',
	EntertainerName: '',
	ContactFirstName: '',
	ContactLastName: '',
	ContactEmailAddress: '',
	ContactPrimaryPhoneNumber: '',
	EntertainerStreetAddress1: '',
	EntertainerStreetAddress2: '',
	EntertainerAddressCity: '',
	EntertainerAddressState: '',
	EntertainerAddressPostcode: '',
	EntertainerABN: '',
	EntertainerActFormats: [],
	EntertainerGenres: [],
	EntertainerSpecialties: [],
	EntertainerMinimumPerformerCount: '',
	EntertainerMaximumPerformerCount: '',
	EntertainerWebsite: '',
	EntertainerFacebook: '',
	EntertainerInstagram: '',
	EntertainerSoundcloud: '',
	EntertainerYoutubeChannel: '',
	EntertainerDescription: '',
	EntertainerRequirements: '',
	EntertainerInsuranceCompany: '',
	EntertainerInsurancePolicyNumber: '',
	EntertainerInsuranceExpiry: '',
	EntertainerPowerSlotsRequired: String(contract.constraints.powerSlots.minimum),
	EntertainerAvailability: [],
	EntertainerPerformanceType: String(
		contract.performancePreferences.find((preference) => preference.code === 'busking')?.value ??
			contract.performancePreferences[0]?.value ??
			'',
	),
	customFile: null,
	botDetector: '',
});

export const normalizeServerErrors = (errors: EntertainerApplicationFieldErrors) => {
	const normalized = { ...errors };
	if (normalized.classifications && !normalized.EntertainerActFormats) {
		normalized.EntertainerActFormats = normalized.classifications;
	}
	if (normalized.performerRange && !normalized.EntertainerMinimumPerformerCount) {
		normalized.EntertainerMinimumPerformerCount = normalized.performerRange;
	}
	if (normalized.promotionLinks && !normalized.EntertainerWebsite) {
		normalized.EntertainerWebsite = normalized.promotionLinks;
	}
	delete normalized.classifications;
	delete normalized.performerRange;
	delete normalized.promotionLinks;
	return normalized;
};

export const isFormFieldName = (field: string): field is keyof EntertainerApplicationFormState =>
	formFieldNames.has(field as keyof EntertainerApplicationFormState);

export const errorEntries = (errors: FieldErrors<EntertainerApplicationFormState>) =>
	Object.entries(errors).flatMap(([field, error]) =>
		typeof error?.message === 'string' ? [[field, error.message] as const] : [],
	);

export const fieldError = (
	errors: FieldErrors<EntertainerApplicationFormState>,
	field: keyof EntertainerApplicationFormState,
) => {
	const message = errors[field]?.message;
	return typeof message === 'string' ? message : undefined;
};

export const focusFirstError = (errors: EntertainerApplicationFieldErrors) => {
	const firstField = Object.keys(errors)[0];
	if (!firstField) return;
	window.requestAnimationFrame(() => document.getElementById(firstField)?.focus());
};

export function useApplicationFormUpdate() {
	const { clearErrors, setValue } = useFormContext<EntertainerApplicationFormState>();
	return useCallback(
		<K extends FieldPath<EntertainerApplicationFormState>>(
			field: K,
			value: FieldPathValue<EntertainerApplicationFormState, K>,
		) => {
			setValue(field, value, { shouldDirty: true });
			if (promotionLinkFields.includes(field as PromotionLinkField)) {
				clearErrors([...promotionLinkFields]);
			} else {
				clearErrors(field);
			}
		},
		[clearErrors, setValue],
	);
}
